import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import axios from 'axios';
import {
  LayoutDashboard, Users, CreditCard, Settings, LogOut, Shield,
  UserCheck, Clock, Lightbulb, CheckCircle, XCircle, Trash2, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { admin, token, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingPayments, setPendingPayments] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/payments?status=pending`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setPendingPayments(paymentsRes.data.length);
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateUserStatus = async (userId, status) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification(`Status atualizado para ${status}`);
      fetchData();
    } catch (error) {
      showNotification('Erro ao atualizar status', 'error');
    }
  };

  const updateUserPlan = async (userId, plan) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}/plan`,
        { plan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification(`Plano atualizado para ${plan}`);
      fetchData();
    } catch (error) {
      showNotification('Erro ao atualizar plano', 'error');
    }
  };

  const deleteUser = async () => {
    if (!deleteUserId) return;
    
    try {
      await axios.delete(`${API}/admin/users/${deleteUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Usuário excluído');
      setDeleteUserId(null);
      fetchData();
    } catch (error) {
      showNotification('Erro ao excluir usuário', 'error');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      trial: 'bg-blue-100 text-blue-800 border-blue-200',
      active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      blocked: 'bg-red-100 text-red-800 border-red-200',
      expired: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    const labels = { 
      pending: 'Pendente', 
      trial: 'Trial', 
      active: 'Ativo', 
      blocked: 'Bloqueado',
      expired: 'Expirado'
    };
    
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {notification.type === 'error' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {notification.message}
        </div>
      )}

      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold">Admin</h1>
              <p className="text-xs text-slate-400">VoiceIdeas</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <Link to="/admin/dashboard">
            <Button 
              variant={location.pathname === '/admin/dashboard' ? 'secondary' : 'ghost'} 
              className="w-full justify-start text-white hover:text-white hover:bg-slate-800"
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-4 h-4 mr-3" />
              Dashboard
            </Button>
          </Link>
          <Link to="/admin/pagamentos">
            <Button 
              variant={location.pathname === '/admin/pagamentos' ? 'secondary' : 'ghost'}
              className="w-full justify-start text-white hover:text-white hover:bg-slate-800"
              data-testid="nav-payments"
            >
              <CreditCard className="w-4 h-4 mr-3" />
              Pagamentos
            </Button>
          </Link>
          <Link to="/admin/configuracoes">
            <Button 
              variant={location.pathname === '/admin/configuracoes' ? 'secondary' : 'ghost'}
              className="w-full justify-start text-white hover:text-white hover:bg-slate-800"
              data-testid="nav-settings"
            >
              <Settings className="w-4 h-4 mr-3" />
              Configurações
            </Button>
          </Link>
        </nav>
        
        <div className="p-4 border-t border-slate-800">
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-3" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          <div>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground">Visão geral da plataforma</p>
          </div>

          {/* Pending Payments Alert */}
          {pendingPayments > 0 && (
            <div className="bg-amber-50 border-2 border-amber-400 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-amber-800">
                    {pendingPayments === 1 
                      ? '1 pagamento aguardando aprovação!' 
                      : `${pendingPayments} pagamentos aguardando aprovação!`}
                  </p>
                  <p className="text-sm text-amber-600">
                    Clique no botão para revisar e liberar os acessos
                  </p>
                </div>
              </div>
              <Link to="/admin/pagamentos">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                  Revisar Pagamentos
                </Button>
              </Link>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total de Usuários</p>
                    <p className="text-3xl font-bold" data-testid="stat-total-users">{stats?.total_users || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Aguardando</p>
                    <p className="text-3xl font-bold" data-testid="stat-pending-users">{stats?.pending_users || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Em Trial</p>
                    <p className="text-3xl font-bold" data-testid="stat-trial-users">{stats?.trial_users || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Ativos</p>
                    <p className="text-3xl font-bold" data-testid="stat-active-users">{stats?.active_users || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Ideias Criadas</p>
                    <p className="text-3xl font-bold" data-testid="stat-total-ideas">{stats?.total_ideas || 0}</p>
                  </div>
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Lightbulb className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Usuários
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>E-mail</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Plano</TableHead>
                      <TableHead>Cadastro</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{getStatusBadge(user.status)}</TableCell>
                        <TableCell>
                          <select 
                            value={user.plan} 
                            onChange={(e) => updateUserPlan(user.id, e.target.value)}
                            className="w-28 h-9 px-3 rounded-md border border-input bg-background text-sm"
                          >
                            <option value="free">Livre</option>
                            <option value="basic">Básico</option>
                            <option value="premium">Premium</option>
                          </select>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(user.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {user.status !== 'active' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-emerald-600"
                                onClick={() => updateUserStatus(user.id, 'active')}
                                data-testid={`approve-user-${user.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {user.status !== 'blocked' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-600"
                                onClick={() => updateUserStatus(user.id, 'blocked')}
                                data-testid={`block-user-${user.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => setDeleteUserId(user.id)}
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Nenhum usuário cadastrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Delete User Modal */}
      {deleteUserId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg">Excluir usuário?</h3>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setDeleteUserId(null)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4">
              <p className="text-muted-foreground">
                Esta ação não pode ser desfeita. Todas as ideias e dados do usuário serão permanentemente excluídos.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <Button variant="outline" onClick={() => setDeleteUserId(null)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={deleteUser}>
                Excluir
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
